import { useQueryClient } from '@tanstack/react-query';
import { theme } from 'antd';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Chat } from 'pages/maxi-ai/Chat';

import { useAccessControl } from 'hooks/auth/useAccessControl';
import { useEntityData } from 'hooks/useEntityData';
import { keyPathToMenuObj, useMenu } from 'hooks/utils/useMenu';

import { Logger } from 'utils/logger';

import {
  useAuthStore,
  useLoadingStore,
  useSidenavCollapseStore,
} from 'store/index';
import { useWorkspaceStore, type Workspace } from 'store/workspace.store';

import { type MenuInfo, Navigation } from 'layout/Navigation';
import { useMenuStyles } from 'layout/hooks/useMenuStyles';

const AppLayout: FC = () => {
  const queryClient = useQueryClient();
  const { workspace, setWorkspace, getAllWorkspaces } = useWorkspaceStore();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);

  // Chain hooks - only fetch user access after workspace is selected
  useEntityData();
  useAccessControl({ enabled: !!workspace });

  const { userAccessibleModules, resetModulesLoaded, modulesLoaded } =
    useAuthStore();
  const { token } = theme.useToken();
  const { styles } = useMenuStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const { links } = useMenu(userAccessibleModules, modulesLoaded);

  const { isSidenavCollapsed, setIsSidenavCollapsed } =
    useSidenavCollapseStore();

  const [showChatBot, setShowChatBot] = useState(false);
  const [defaultOpen, setDefaultOpen] = useState<string[]>(['accounting']);
  const [defaultSelected, setDefaultSelected] = useState<string[]>(['revenue']);
  const [userCollapsedMenus, setUserCollapsedMenus] = useState<Set<string>>(
    new Set()
  );

  const prevWidthRef = useRef(window.innerWidth);
  const isSystemUpdateRef = useRef(false);
  const collapsedRef = useRef(false);
  const currentSectionRef = useRef<string>('');
  const fetchAttempts = useRef(0);
  const isFetchingRef = useRef(false);
  const bridgeLoadingIdRef = useRef<string | null>(null);
  const MAX_ATTEMPTS = 3;
  const RETRY_DELAY = 2000; // 2 seconds between attempts

  const menuStyle = useMemo(
    () => ({
      borderRight: 'none',
    }),
    []
  );

  const toggleCollapsed = useCallback(() => {
    setIsSidenavCollapsed(!isSidenavCollapsed);
  }, [isSidenavCollapsed, setIsSidenavCollapsed]);

  const handleMenuClick = useCallback(
    (item: MenuInfo) => {
      const menuItem = keyPathToMenuObj([...item.keyPath].reverse());
      if (!menuItem) return;
      const navigationPath = menuItem.path || menuItem.permissionKey;
      if (navigationPath) {
        navigate(navigationPath);
      }
      // When a menu item is clicked, we need to ensure that both the item and its parent are selected
      if (item.keyPath) {
        setDefaultSelected(item.keyPath);
      }
    },
    [navigate]
  );

  // Wrapper for workspace changes that resets modules and clears query cache
  const handleWorkspaceChange = useCallback(
    (newWorkspace: Workspace) => {
      resetModulesLoaded();
      setWorkspace(newWorkspace);
      // Clear all cached queries to prevent stale data (doesn't trigger refetches)
      queryClient.clear();
      // Navigate to home - RedirectComponent will find first available path for new workspace
      navigate('/', { replace: true });
    },
    [resetModulesLoaded, setWorkspace, queryClient, navigate]
  );

  // Helper function for system-initiated updates
  const updateDefaultOpenSystematic = (newKeys: string[]) => {
    isSystemUpdateRef.current = true;
    setDefaultOpen(newKeys);
    // Reset flag after React's state update cycle
    setTimeout(() => {
      isSystemUpdateRef.current = false;
    }, 0);
  };

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const fetchWorkspaces = async () => {
      // Prevent concurrent fetches
      if (isFetchingRef.current) return;

      if (fetchAttempts.current >= MAX_ATTEMPTS) {
        Logger.warn('Maximum workspace fetch attempts reached');
        return;
      }

      isFetchingRef.current = true;

      try {
        // Single loader for entire initialization flow
        bridgeLoadingIdRef.current = useLoadingStore
          .getState()
          .startLoading(
            'Pulling User Data',
            'Fetching user data and resources...'
          );

        const fetchedWorkspaces = await getAllWorkspaces();
        fetchAttempts.current += 1;

        // Always update state with whatever we get, even if empty
        setWorkspaces(fetchedWorkspaces);

        // If we don't have a workspace selected yet, try to find Facilio workspace or use first available
        if (!workspace) {
          const facilioWorkspace = fetchedWorkspaces.find(
            (w) => w?.id === '1918bf5d-b288-44b9-9122-e1118cad9f4f'
          );
          const selectedWorkspace = facilioWorkspace ?? fetchedWorkspaces[0];

          if (selectedWorkspace) {
            setWorkspace(selectedWorkspace);
            // Query will auto-fetch when workspace is set (enabled depends on workspace)
          }
        }

        // Retry if empty and haven't hit max attempts
        if (
          fetchedWorkspaces.length === 0 &&
          fetchAttempts.current < MAX_ATTEMPTS
        ) {
          timeoutId = setTimeout(fetchWorkspaces, RETRY_DELAY);
        }
      } catch (error) {
        Logger.error('Error fetching workspaces:', error);
        if (fetchAttempts.current < MAX_ATTEMPTS) {
          timeoutId = setTimeout(fetchWorkspaces, RETRY_DELAY);
        }
      } finally {
        isFetchingRef.current = false;
        // Stop bridge loader when everything is done
        if (bridgeLoadingIdRef.current) {
          useLoadingStore.getState().stopLoading(bridgeLoadingIdRef.current);
        }
      }
    };

    // Only fetch workspaces if we don't have any loaded yet
    if (workspaces?.length === 0) {
      fetchWorkspaces();
    }

    // Cleanup function for the effect
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (bridgeLoadingIdRef.current) {
        useLoadingStore.getState().stopLoading(bridgeLoadingIdRef.current);
      }
    };
  }, [workspace, getAllWorkspaces, setWorkspace, workspaces]);

  // Update menu selection based on current path - only runs when location or links change
  useEffect(() => {
    const { pathname } = location;
    const currentPath = pathname.replace(/^\//, '');

    if (!currentPath || links.length === 0) return;

    // Find matching menu item
    for (const link of links) {
      if (link.children) {
        for (const child of link.children) {
          if (
            child.path &&
            (currentPath === child.path ||
              currentPath.startsWith(child.path + '/'))
          ) {
            const newSection = link.permissionKey;

            // Reset collapsed menus when navigating to different section
            if (
              currentSectionRef.current !== newSection &&
              currentSectionRef.current !== ''
            ) {
              setUserCollapsedMenus(new Set());
            }
            currentSectionRef.current = newSection;

            if (!isSidenavCollapsed) {
              updateDefaultOpenSystematic([newSection]);
            }

            setDefaultSelected([child.permissionKey]);
            return;
          }
        }
      } else if (
        link.path &&
        (currentPath === link.path || currentPath.startsWith(link.path + '/'))
      ) {
        const newSection = link.permissionKey;

        // Reset collapsed menus when navigating to different section
        if (
          currentSectionRef.current !== newSection &&
          currentSectionRef.current !== ''
        ) {
          setUserCollapsedMenus(new Set());
        }
        currentSectionRef.current = newSection;

        setDefaultSelected([link.permissionKey]);
        return;
      }
    }
  }, [location, links, isSidenavCollapsed]);

  // Hide / show open keys when collapsed - Enhanced to handle edge cases

  useEffect(() => {
    // When sidebar collapses, close all menus but preserve user preferences
    if (isSidenavCollapsed && defaultOpen.length > 0 && !collapsedRef.current) {
      collapsedRef.current = true;

      // Safe: syncing menu state with sidebar collapse/expand
      setDefaultOpen([]);
      // Note: We don't clear userCollapsedMenus here - user preferences persist
    }

    // When sidebar expands, restore appropriate menus based on current location and user preferences
    if (
      !isSidenavCollapsed &&
      collapsedRef.current &&
      !defaultOpen.includes(defaultSelected[0])
    ) {
      // Only auto-open if user hasn't explicitly collapsed this menu
      if (!userCollapsedMenus.has(defaultSelected[0])) {
        collapsedRef.current = false;
        setDefaultOpen([defaultSelected[0]]);
      } else {
        collapsedRef.current = false;
        // Keep menu closed per user preference
      }
    }
  }, [isSidenavCollapsed, defaultSelected, defaultOpen, userCollapsedMenus]);

  // Dynamically collapse and expand navigation when above and below 1440px
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const checkResize = (delay: number) => {
      if (timeoutId) clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        const viewportWidth = window.innerWidth;
        const prevWidth = prevWidthRef.current;

        const isSmall = viewportWidth < 1440;
        const wasSmall = prevWidth < 1440;

        if (isSmall !== wasSmall) {
          setIsSidenavCollapsed(isSmall);
        }

        prevWidthRef.current = viewportWidth;
      }, delay);
    };

    const handleResize = () => checkResize(100);
    const handleOrientationChange = () => checkResize(300);

    // Initial check
    // We only force collapse if:
    // 1. The screen is small (< 1440)
    // 2. AND there is no persisted user preference yet
    const hasPersistedState =
      typeof window !== 'undefined' &&
      !!localStorage.getItem('sidenav-collapse-storage');

    if (window.innerWidth < 1440 && !hasPersistedState) {
      setIsSidenavCollapsed(true);
    }
    prevWidthRef.current = window.innerWidth;

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [setIsSidenavCollapsed]);

  return (
    <div className="flex h-full" style={{ background: token.colorBgContainer }}>
      <nav
        className="max-w-[240px] border-r"
        style={{
          borderColor: token.colorBorder,
          // Safari-specific fixes for smooth transitions
          WebkitTransform: 'translateZ(0)',
          transform: 'translateZ(0)',
          WebkitBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden',
          transition: 'all 0.3s ease',
          WebkitTransition: 'all 0.3s ease',
        }}
      >
        <Navigation
          setShowChatBot={setShowChatBot}
          isSidenavCollapsed={isSidenavCollapsed}
          toggleCollapsed={toggleCollapsed}
          workspace={workspace}
          workspaces={workspaces}
          setWorkspace={handleWorkspaceChange}
          links={links}
          defaultOpen={defaultOpen}
          setDefaultOpen={setDefaultOpen}
          defaultSelected={defaultSelected}
          menuStyle={menuStyle}
          handleMenuClick={handleMenuClick}
          styles={styles}
          userCollapsedMenus={userCollapsedMenus}
          setUserCollapsedMenus={setUserCollapsedMenus}
          isSystemUpdateRef={isSystemUpdateRef}
        />
      </nav>

      <main className="flex-1 h-dvh relative overflow-hidden">
        <Outlet />
        {showChatBot && <Chat onClose={() => setShowChatBot(false)} />}
      </main>
    </div>
  );
};

export { AppLayout };
